import React, { useMemo } from 'react';
import { State } from 'state';
import { useDispatch, useSelector } from 'react-redux';
import {
  Appearance,
  ContentType,
  Folder,
  IAdvancedFilters,
  IModerationHotKeysState,
  IModerationMessagesState,
  IModerationPostsState,
  IModerationSearchState,
  IModerationUserState,
  IPageInfo,
  Message,
  ModerationSearchType,
  PerspectiveTag,
  TextAnalyzerTag
} from './state/types';
import Dashboard from './dashboard';
import getSpotItems from './state/actions/get-spot-items';
import changePageInfoAction from './state/actions/change-page-info';
import approveItemsAction, { Options as MessageActionOptions } from './state/actions/approve-items';
import rejectItemsAction from './state/actions/reject-items';
import searchAction from './state/actions/search';
import setConversationPolicyAction from './state/actions/set-conversation-policy';
import {
  changePageFilters as changePageFiltersAction,
  clearAllItems as clearAllItemsAction,
  closeModerationActiveBar as closeModerationActiveBarAction,
  openModerationActiveBar as openModerationActiveBarAction,
  resetAllFilters as resetAllFiltersAction,
  resetModerationFilters as resetModerationFiltersAction,
  selectAllItems as selectAllItemsAction,
  setClientAppearance as setClientAppearanceAction,
  toggleItem as toggleItemAction,
  toggleModerationActiveBar as toggleModerationActiveBarAction,
  toggleModerationMode as toggleModerationModeAction
} from './state/actions';
import { openModal as openModalAction } from './modals/state/actions';
import fetchMessageTagsSpans from './state/actions/fetch-message-tags-spans';

interface Props {
  currentRoute: any;
  spotLanguage: string;
  currentUser: Object;
  spotId: string;
  inpageSkin: 'light' | 'dark';
  contentType: ContentType;
  isMultipleModeratorsMode: boolean;
  isModerationViewer: boolean;
}

export interface IModerationUserActions {
  fetchSpotItems: (contentType?: ContentType, withReleaseLock?: boolean) => void;
  changePageInfo: (pageInfo: Partial<IPageInfo>, forceUpdate?: boolean) => void;
  selectAllItems: () => void;
  clearAllItems: () => void;
  toggleItem: (props: { id: string; checked: boolean; cursor: number }) => void;
  resetAllFilters: (props?: {
    pageInfo?: Partial<IPageInfo>;
    filters?: Partial<IAdvancedFilters>;
    search?: { searchType: ModerationSearchType; searchValue: string };
    isLoading?: boolean;
  }) => void;
  resetModerationFilters: () => void;
  approveItems: (messages: Message[], options: MessageActionOptions) => void;
  rejectItems: (messages: Message[], options: MessageActionOptions) => void;
  setClientAppearance: (props: { id: string; appearance: Appearance; folder: Folder }) => void;
  search: (props: { type: ModerationSearchType; value: string; userId?: string }) => void;
  changePageFilters: (props: {
    filters: Partial<IAdvancedFilters>;
    search: { searchValue: string; searchType: ModerationSearchType };
    preventOffsetReset?: boolean;
  }) => void;
  toggleModerationActiveBar: () => void;
  openModerationActiveBar: () => void;
  closeModerationActiveBar: () => void;
  openModal: (modalType: string, modalProps: any) => void;
  setConversationPolicy: (props) => void;
  toggleModerationMode: () => void;
  setHighlightTag: (
    message: Message,
    tagName: TextAnalyzerTag,
    vendorType: PerspectiveTag.GOOGLE_PERSPECTIVE
  ) => void;
}

export interface IDashboardProps
  extends IModerationMessagesState,
    IModerationUserState,
    IModerationPostsState,
    IModerationSearchState,
    IModerationUserActions,
    Props {
  isHighlightTagsEnabled: boolean;
  totalItems: number;
  hotKeysSettings: IModerationHotKeysState;
}

const CommentsAndPostsModeration: React.FC<Props> = ({
  currentRoute,
  currentUser,
  spotId,
  inpageSkin,
  isMultipleModeratorsMode,
  contentType,
  spotLanguage,
  isModerationViewer
}) => {
  const { posts, users, messages, search, hotKeysSettings, isHighlightTagsEnabled } = useSelector(
    (state: State) => ({
      messages: state.moderation.messages,
      posts: state.moderation.posts,
      users: state.moderation.users,
      search: state.moderation.search,
      hotKeysSettings: state.moderation.hotKeys,
      isHighlightTagsEnabled: state.moderation.messages.isHighlightTagsEnabled
    })
  );
  const dispatch = useDispatch();

  const userActions: IModerationUserActions = useMemo(
    () => ({
      fetchSpotItems: (contentType?: ContentType, withReleaseLock?: boolean) =>
        dispatch(getSpotItems(contentType, withReleaseLock)),
      changePageInfo: (pageInfo: Partial<IPageInfo>, forceUpdate: boolean) =>
        dispatch(changePageInfoAction(pageInfo, forceUpdate)),
      selectAllItems: () => dispatch(selectAllItemsAction()),
      clearAllItems: () => dispatch(clearAllItemsAction()),
      toggleItem: (props: { id: string; checked: boolean; cursor: number }) =>
        dispatch(toggleItemAction(props)),
      resetAllFilters: (props = {}) => dispatch(resetAllFiltersAction(props)),
      resetModerationFilters: () => dispatch(resetModerationFiltersAction()),
      approveItems: (messages: Message[], options: MessageActionOptions) =>
        dispatch(approveItemsAction(messages, options)),
      rejectItems: (messages: Message[], options: MessageActionOptions) =>
        dispatch(rejectItemsAction(messages, options)),
      setClientAppearance: props => dispatch(setClientAppearanceAction(props)),
      search: props => dispatch(searchAction(props)),
      changePageFilters: props => dispatch(changePageFiltersAction(props)),
      toggleModerationActiveBar: () => dispatch(toggleModerationActiveBarAction()),
      openModerationActiveBar: () => dispatch(openModerationActiveBarAction()),
      closeModerationActiveBar: () => dispatch(closeModerationActiveBarAction()),
      openModal: (modalType: string, modalProps: any) =>
        dispatch(openModalAction({ modalType, modalProps })),
      setConversationPolicy: props => dispatch(setConversationPolicyAction(props)),
      toggleModerationMode: (props: Parameters<typeof toggleModerationModeAction>[0] = {}) => dispatch(toggleModerationModeAction(props)),
      setHighlightTag: (
        message: Message,
        tagName: TextAnalyzerTag,
        vendorType: PerspectiveTag.GOOGLE_PERSPECTIVE
      ) => dispatch(fetchMessageTagsSpans(message, tagName, vendorType))
    }),
    [dispatch]
  );

  // Here we can create some unique props based on contentType and pass them to Dashboard.
  // That way, we can create different views between posts and comments moderation tabs.

  const props: IDashboardProps = {
    currentRoute,
    currentUser,
    contentType,
    inpageSkin,
    isMultipleModeratorsMode,
    spotId,
    spotLanguage,
    isModerationViewer,
    ...posts,
    ...messages,
    ...users,
    ...search,
    isHighlightTagsEnabled,
    hotKeysSettings,
    totalItems: messages.counts[messages.pageInfo.folder] || 0,
    ...userActions
  };

  return <Dashboard {...props} />;
};

export default CommentsAndPostsModeration;
